// React core imports
import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
// Redux
import { useSelector, useDispatch } from 'react-redux';
// Third-party libraries
import { Row, Col, Dropdown, Form, Button, OverlayTrigger, Tooltip, Alert } from 'react-bootstrap';
import moment from 'moment';
import { AnimatePresence, motion } from 'framer-motion';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
// Utility functions
import { cleanShippingId, startUpperCase, initialsUpperCase } from 'utils/textFormat';
import { handleCatchError, handleAPIError } from 'utils/handleErrors';

// UI Components
import HtmlHead from 'components/html-head/HtmlHead';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
import Calendar from 'components/calendars/Calendar';
import ShippingCSV from 'components/reactCsv/ShippingCSV';
import AdminLoader from 'components/loading/AdminLoader';
import LoadingPulse from 'components/loading/LoadingPulse';
import ShippingCard from './components/ShippingCard';
import Paginate from '../../components/Paginate';
import EmptyList from '../../components/EmptyList';
import DateModal from './components/DateModal';
// Redux Actions
import {
  setPage,
  setItemsPerPage,
  setUserId,
  setShippingId,
  setService,
  setCarrier,
  setCompany,
  setInitialDate,
  setEndDate,
  setTrackingStatus,
  resetAllShippingsFilters,
  setFilteredMode,
} from '../../../slices/uiStates/allShippingsSlice';

// API functions
import { getAdminFilteredShippings, getShippingCompanies, getShippingCodes, getShippingBrokerCodes, getAdminShippingsPerDate } from '../../api/Api-admin';

const { FormatMoney } = require('format-money-js');

const Shippings = () => {
  const title = 'Historial de Envíos';
  const description = 'Historial Envíos Plataforma Autopaquete';
  // - Use references to do scroll Up
  const startRef = useRef(null);
  const dispatch = useDispatch();
  // - Global State from Store.js
  const { page, itemsPerPage, userId, shippingId, service, receiverName, carrier, company, initialDate, endDate, filteredMode, trackingStatus } = useSelector(
    (state) => state.all_shippings
  );
  // - Local States
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoader, setIsInitialLoader] = useState(true);
  const [shippings, setShippings] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updatePage, setUpdatePage] = useState(false);
  // Start Pagination States
  const [pageView, setPageView] = useState(page); // for the view
  const [itemsPerPageView, setItemsPerPageView] = useState(itemsPerPage); // for the view
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(null); // update with the response of request
  const [serviceList, setServiceList] = useState([]);
  const [brokerCarrierList, setBrokerCarrierList] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [filteredSuccess, setFilteredSuccess] = useState('');
  const [debouncedIdentifier, setDebouncedIdentifier] = useState(shippingId);
  const [showConfetti, setShowConfetti] = useState(false);
  const windowSize = useWindowSize();
  const fullHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight, windowSize.height);
  const [filters, setFilters] = useState({
    IdShipping: false,
    IdUser: false,
    initialDate: false,
    endDate: false,
    service: false,
    supplier: false,
    tracking: false,
  });
  const fm = new FormatMoney({
    decimals: 0, // No decimales
  });
  const AlertFilter = () => <em className="bg-foreground p-1 rounded-3 position-absolute bottom-50 end-0 text-small text-secondary">desactivado</em>;
  const cardVariants = {
    hidden: { opacity: 0, y: 50 }, // Empieza invisible y desplazado hacia abajo
    visible: { opacity: 1, y: 0 }, // Se vuelve visible y se posiciona
  };
  const settingPage = (record) => {
    setIsLoading(true);
    dispatch(setPage(1));
    dispatch(setItemsPerPage(record));
  };
  // Get All Shippings per date
  const handlerGetShippingsPerDate = async () => {
    try {
      setIsLoading(true);
      const response = await getAdminShippingsPerDate(page, itemsPerPage, initialDate, endDate);
      // console.log('get admin all shippings per date res:', response);
      if (response.shippings !== undefined) {
        setShippings(response.shippings);
        setTotalPages(response.total_pages);
        setTotalItems(response.total_items);
        // LOCAL VIEW VALUES UPDATE
        setPageView(response.current_page);
        setItemsPerPageView(response.page_size);
        if (startRef.current) {
          startRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      } else if (response.error) {
        handleAPIError(response);
      }
    } catch (error) {
      // console.log('error catch get all shippings');
      handleCatchError(error);
    } finally {
      setIsLoading(false);
      setIsInitialLoader(false);
    }
  };
  // Get filtered shippings
  const handlerGetFilteredShippings = async () => {
    try {
      setIsLoading(true);
      const serviceClean = service.replace(/\s+/g, '_'); // add underscores to make request
      const response = await getAdminFilteredShippings(
        page,
        itemsPerPage,
        shippingId,
        userId,
        receiverName,
        serviceClean,
        carrier,
        company,
        initialDate,
        endDate,
        trackingStatus.request
      );
      if (response.shippings !== undefined) {
        // console.log('Envios filtrados res:', response);
        setShippings(response.shippings);
        setItemsPerPageView(response.page_size); // actualizar la vista de la paginacion
        setPageView(response.current_page); // para actualizar la vista
        setTotalPages(response.total_pages);
        setTotalItems(response.total_items);
        setFilteredSuccess(
          <>
            Filtro
            {trackingStatus?.view && (
              <>
                {' '}
                Rastreo de envío ▸ <strong>{trackingStatus.view}</strong>
              </>
            )}
            {shippingId && (
              <>
                {' '}
                Id de envío ▸ <strong>{shippingId}</strong>
              </>
            )}
            {company && (
              <>
                {' '}
                Proveedor ▸ <strong>{company}</strong>
              </>
            )}
            {userId && (
              <>
                {' '}
                Id de usuario ▸ <strong>{userId}</strong>
              </>
            )}
            {receiverName && (
              <>
                {' '}
                Nombre del destinatario ▸ <strong>{receiverName}</strong>
              </>
            )}
            {carrier && (
              <>
                {' '}
                Paquetería ▸ <strong>{startUpperCase(carrier)}</strong>
              </>
            )}
            {service && (
              <>
                {' '}
                Servicio ▸ <strong>{initialsUpperCase(service)}</strong>
              </>
            )}
            {initialDate && (
              <>
                {' '}
                del <strong>{moment(initialDate).format('DD-MM-YY')}</strong>
              </>
            )}
            {endDate && (
              <>
                {' '}
                al <strong>{moment(endDate).format('DD-MM-YY')}</strong>
              </>
            )}
          </>
        );
        if (startRef.current) {
          startRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      } else if (response.error) {
        setShippings(shippings || []);
        setTotalPages(0);
        // setFilteredSuccess(''); // para eliminar el mensaje "Filtro por servicio..."
        handleAPIError(response);
      }
    } catch (error) {
      handleCatchError(error);
    } finally {
      setIsLoading(false);
      dispatch(setFilteredMode(true)); // respuesta exitosa o no es necesario para mantener el modo filtrando y que aparezca el btn regresar
      setIsInitialLoader(false);
    }
  };
  const resetInitialMode = () => {
    dispatch(resetAllShippingsFilters());
    setFilteredSuccess(''); // para eliminar el mensaje "Filtro por nombre..."
    setUpdatePage(!updatePage);
  };
  const deactivateFiltersById = () => {
    dispatch(setUserId(''));
    dispatch(setCarrier(''));
    dispatch(setCompany(''));
    dispatch(setService(''));
    dispatch(setInitialDate(''));
    dispatch(setEndDate(''));
    dispatch(setTrackingStatus({ view: '', request: '' }));
    setFilters({
      IdShipping: false,
      IdUser: true,
      initialDate: true,
      company: true,
      endDate: true,
      service: true,
      supplier: true,
      tracking: true,
    });
  };
  // Listener to filter by tracking state
  const handleFirstClick = (e, state) => {
    dispatch(setTrackingStatus({ view: e.target.innerText, request: state }));
  };
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedIdentifier(shippingId);
    }, 200); // 500 ms de espera
    return () => clearTimeout(handler);
  }, [shippingId]);
  useEffect(() => {
    if (debouncedIdentifier) {
      deactivateFiltersById();
    } else {
      dispatch(setInitialDate(new Date().toISOString()));
      dispatch(setEndDate(new Date().toISOString()));
      setFilters({
        IdShipping: false,
        IdUser: false,
        initialDate: false,
        endDate: false,
        service: false,
        supplier: false,
      });
    }
  }, [debouncedIdentifier]);
  useEffect(() => {
    getShippingCompanies().then((response) => {
      if (response?.list) {
        setSupplierList(response?.list);
      } else {
        setSupplierList([]);
      }
    });
    getShippingCodes().then((response) => {
      if (response?.list) {
        setServiceList(response?.list);
      } else {
        setServiceList([]);
      }
    });
    getShippingBrokerCodes().then((response) => {
      if (response?.list) {
        setBrokerCarrierList(response?.list);
      } else {
        setBrokerCarrierList([]);
      }
    });
  }, []);
  // - Whenever the pagination status change
  useEffect(() => {
    if (filteredMode) {
      handlerGetFilteredShippings();
    } else {
      handlerGetShippingsPerDate();
    }
  }, [page, itemsPerPage, updatePage]);

  setTimeout(() => {
    if (showConfetti) {
      setShowConfetti(false);
    }
  }, 60000); // 2 minutos

  return (
    <>
      {isLoading && <LoadingPulse />}
      {showConfetti && (
        <Confetti
          numberOfPieces={200}
          width={windowSize.width}
          height={fullHeight}
          run
          colors={['#f8a29e', '#f25460', '#91dacb', '#3d3e40', '#FFD700', '#FFEA00', '#00BFFF', '#32CD32', '#FF69B4', '#FF8C00']}
        />
      )}
      {isModalOpen && <DateModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />}
      {isInitialLoader ? (
        <AdminLoader />
      ) : (
        <>
          <HtmlHead title={title} description={description} />
          <div className="page-title-container mb-sm-5" ref={startRef}>
            <Row className="g-0">
              {/* Title Start */}
              <Col className="col-auto mb-3 mb-sm-0 me-auto order-1">
                <NavLink className="muted-link pb-1 d-inline-block hidden breadcrumb-back invisible" to="/">
                  <CsLineIcons icon="chevron-left" size="13" />
                  <span className="align-middle text-small ms-1">Envíos</span>
                </NavLink>
                <h1 className="mb-0 pb-0 display-4" id="title">
                  {title}
                </h1>
              </Col>
              {/* Title End */}
            </Row>
          </div>
          {/* este div es para ocultar el contenido cuando esta cargando  */}
          <div style={{ minHeight: '50vh' }} id="cardContainer">
            {/* - Filters Start */}
            <Row className="g-0 mb-2 bg-successx">
              {/*  by ids */}
              <Col xs="12" sm="auto" className="me-2 mb-2 position-relative">
                <Dropdown className="w-100">
                  <Dropdown.Toggle
                    disabled={filters.supplier}
                    variant="foreground-alternate"
                    className="text-end shadow d-flex justify-content-between align-items-center w-100"
                    style={{
                      fontFamily: 'Montserrat',
                      minWidth: '155px',
                    }}
                  >
                    {carrier || 'Paqueteria'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="shadow" align="end" style={{ fontFamily: 'Montserrat', maxHeight: '270px', overflowY: 'scroll' }}>
                    <Dropdown.Item onClick={() => dispatch(setCarrier(''))} className={carrier ? 'd-block' : 'd-none'}>
                      Ninguno
                    </Dropdown.Item>
                    {supplierList?.map((element) => (
                      <Dropdown.Item key={element} onClick={() => dispatch(setCarrier(startUpperCase(element)))}>
                        {startUpperCase(element)}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                {filters.supplier && <AlertFilter />}
              </Col>
              <Col xs="12" sm="auto" className="me-2 mb-2 position-relative">
                <Dropdown className="w-100">
                  <Dropdown.Toggle
                    disabled={filters.company}
                    variant="foreground-alternate"
                    className="text-end shadow d-flex justify-content-between align-items-center w-100"
                    style={{
                      fontFamily: 'Montserrat',
                      minWidth: '155px',
                    }}
                  >
                    {company || 'Proveedor'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="shadow" align="end" style={{ fontFamily: 'Montserrat', maxHeight: '270px', overflowY: 'scroll' }}>
                    <Dropdown.Item onClick={() => dispatch(setCompany(''))} className={company ? 'd-block' : 'd-none'}>
                      Ninguno
                    </Dropdown.Item>
                    {brokerCarrierList?.map((element) => (
                      <Dropdown.Item key={element} onClick={() => dispatch(setCompany(startUpperCase(element)))}>
                        {startUpperCase(element)}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                {filters.company && <AlertFilter />}
              </Col>
              <Col xs="12" sm="auto" className="me-2 mb-2 position-relative">
                <Dropdown className="d-inline-block w-100">
                  <Dropdown.Toggle
                    disabled={filters.tracking}
                    variant="foreground-alternate"
                    className="shadow d-flex align-items-center justify-content-between w-100"
                    style={{ fontFamily: 'Montserrat', minWidth: '250px' }}
                  >
                    {trackingStatus.view || 'Rastreo'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="shadow sw-auto" style={{ minHeight: '100px' }}>
                    {trackingStatus.view && <Dropdown.Item onClick={() => dispatch(setTrackingStatus({ view: '', request: '' }))}>NINGUNO</Dropdown.Item>}
                    <Dropdown.Item onClick={(e) => handleFirstClick(e, 'CREATED_LABEL')}>ETIQUETA CREADA</Dropdown.Item>
                    <Dropdown.Item onClick={(e) => handleFirstClick(e, 'ON_THE_WAY')}>EN CAMINO</Dropdown.Item>
                    <Dropdown.Item onClick={(e) => handleFirstClick(e, 'PROCESS_DELIVERY')}>PROCESO DE ENTREGA</Dropdown.Item>
                    <Dropdown.Item onClick={(e) => handleFirstClick(e, 'DELIVERED')}>ENTREGADO</Dropdown.Item>
                    <Dropdown.Item onClick={(e) => handleFirstClick(e, 'EXCEPTION')}>EXCEPCIÓN</Dropdown.Item>
                    <Dropdown.Item onClick={(e) => handleFirstClick(e, 'RETURNED')}>RETORNADO</Dropdown.Item>
                    <Dropdown.Item onClick={(e) => handleFirstClick(e, 'UPDATED_DELIVERY')}>ENTREGA ACTUALIZADA</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                {filters.tracking && <AlertFilter />}
              </Col>
              <Col xs="12" sm="auto" className="me-2 mb-2 position-relative">
                <Dropdown>
                  <Dropdown.Toggle
                    disabled={filters.service}
                    variant="foreground-alternate"
                    className="shadow d-flex justify-content-between align-items-center w-100"
                    style={{ fontFamily: 'Montserrat', minWidth: '250px' }}
                  >
                    {service || 'Servicio'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="shadow dropdown-menu-end" style={{ fontFamily: 'Montserrat', maxHeight: '250px', overflowY: 'scroll' }}>
                    <Dropdown.Item onClick={() => dispatch(setService(''))} className={service ? 'd-block' : 'd-none'}>
                      Ninguno
                    </Dropdown.Item>
                    {serviceList?.map((item) => (
                      <Dropdown.Item key={item} onClick={() => dispatch(setService(initialsUpperCase(item)))}>
                        {initialsUpperCase(item)}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                {filters.service && <AlertFilter />}
              </Col>
              {/* by services info */}
              <Row className="g-0">
                <Col xs="12" md="9" lg="12" className="d-flex justify-content-start bg-warningx">
                  <Row className="g-0">
                    <Col xs="12" sm="auto" lg="auto" className="me-2 mb-1 position-relative">
                      <Form.Control
                        type="text"
                        style={{ fontFamily: 'Montserrat', minWidth: '250px' }}
                        placeholder="ID Envío"
                        readOnly={filters.IdShipping}
                        value={shippingId}
                        onChange={(e) => {
                          dispatch(setShippingId(cleanShippingId(e.target.value)));
                        }}
                      />
                    </Col>
                    <Col xs="12" sm="auto" className="me-2 d-inline-block mb-0 position-relative">
                      <Form.Control
                        type="text"
                        className="mb-2"
                        style={{ fontFamily: 'Montserrat', minWidth: '150px' }}
                        placeholder="ID Usuario"
                        readOnly={filters.IdUser}
                        value={userId}
                        onChange={(e) => dispatch(setUserId(e.target.value))}
                      />
                      {filters.IdUser && <AlertFilter />}
                    </Col>
                    <Col xs="12" sm="5" lg="3" className="me-2 mb-2 position-relative">
                      <Calendar isDisabled={filters.initialDate} selectDate={initialDate} setState={setInitialDate} holder="Fecha Inicial" width="150px" />
                      {filters.initialDate && <AlertFilter />}
                    </Col>
                    <Col xs="12" sm="5" lg="3" className="me-2 mb-2 bg-successm position-relative">
                      <Calendar isDisabled={filters.endDate} selectDate={endDate} setState={setEndDate} holder="Fecha Final" width="150px" />
                      {filters.endDate && <AlertFilter />}
                    </Col>
                  </Row>
                </Col>
              </Row>
              {/* buttons  */}
              <Row className="g-0 mt-2">
                <Col xs="12" sm="6" className="bg-warningx">
                  <Button
                    variant="primary"
                    style={{ height: '36px' }}
                    className="mb-0 me-2"
                    onClick={() => {
                      if (page > 1) {
                        // la primera vez el estado filteredMode es falso
                        dispatch(setFilteredMode(true)); // para que el useEffect funcione bien
                        dispatch(setPage(1)); // ejecutara el useEffect que decide que fn ejecutar pero con la pag 1
                      } else {
                        handlerGetFilteredShippings();
                      }
                    }}
                  >
                    <CsLineIcons icon="filter" size="15" className="mb-1 me-1" />
                    <span>Filtrar</span>
                  </Button>
                  {filteredMode && (
                    <OverlayTrigger delay={{ show: 100, hide: 0 }} placement="top" overlay={<Tooltip id="tooltip-top">Limpiar filtro</Tooltip>}>
                      <Button variant="outline-primary" style={{ height: '36px' }} onClick={resetInitialMode}>
                        <CsLineIcons icon="refresh-horizontal" size="15" className="mb-1 me-1" />
                        {/* <span>Regresar</span> */}
                      </Button>
                    </OverlayTrigger>
                  )}
                </Col>
                <Col xs="12" sm="6" className="mt-5 mt-sm-0 d-flex justify-content-end bg-infox">
                  {shippings.length > 5 && <ShippingCSV shippings={shippings} crmId={userId} />}
                  <Dropdown align={{ xs: 'end' }} className={`ms-2 d-inline-block ${shippings.length <= 5 && 'd-none'}`}>
                    <Dropdown.Toggle variant="foreground-alternate" className="shadow sw-15">
                      {`${itemsPerPageView} Envios`}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="shadow dropdown-menu-end">
                      <Dropdown.Item className={`${itemsPerPageView === 20 && 'd-none'}`} onClick={() => settingPage(20)}>
                        20 Envíos
                      </Dropdown.Item>
                      <Dropdown.Item className={`${itemsPerPageView === 50 && 'd-none'}`} onClick={() => settingPage(50)}>
                        50 Envíos
                      </Dropdown.Item>
                      <Dropdown.Item className={`${itemsPerPageView === 100 && 'd-none'}`} onClick={() => settingPage(100)}>
                        100 Envíos
                      </Dropdown.Item>
                      <Dropdown.Item className={`${itemsPerPageView === 200 && 'd-none'}`} onClick={() => settingPage(200)}>
                        200 Envíos
                      </Dropdown.Item>
                      <Dropdown.Item className={`${itemsPerPageView === 300 && 'd-none'}`} onClick={() => settingPage(300)}>
                        300 Envíos
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
              </Row>
            </Row>
            {/* - Filters End */}
            <hr className="mt-3 mb-4 mt-xl-5 bg-muted" />
            {/* *** Main Page Start *** */}
            {/* List Header Start */}
            {filteredSuccess && <div className="text-muted mb-3">{filteredSuccess}</div>}
            <div className={`d-flex justify-content-between mb-3 text-muted ${shippings?.length < 2 && 'd-none'}`}>
              <span>
                Total: <strong>{fm.from(totalItems)}</strong> {`envíos ${!filteredMode ? 'del día' : 'encontrados'} `}
              </span>
              <span>
                Página <strong>{pageView}</strong>
              </span>
            </div>
            <Row className={`g-0 mb-2 d-none d-xl-flex pt-2 pb-4 py-xl-0 ps-5 pe-4 custom-sort h-100 ${shippings.length === 0 && 'invisible'} `}>
              <Col xl="3" className="d-flex flex-column mb-xl-0 pe-3">
                <div className="text-muted text-small cursor-pointer">USUARIO / ID USUARIO</div>
              </Col>
              <Col xl="2" className="d-flex flex-column pe-1 justify-content-center align-items-center">
                <div className="text-muted text-small cursor-pointer">FECHA / HORA</div>
              </Col>
              <Col xl="3" className="d-flex flex-column align-items-start mb-xl-0 pe-3">
                <div className="text-muted text-small cursor-pointer">SERVICIO / ID ENVÍO</div>
              </Col>
              <Col xl="2" />
              <Col xl="1" className="d-flex flex-column pe-1 align-items-end justify-content-center">
                <div className="invisible text-muted text-small cursor-pointer">MODAL</div>
              </Col>
              <Col xl="1" className="d-flex flex-column pe-1 justify-content-center align-items-end">
                <div className="text-muted text-small cursor-pointer">COSTO</div>
              </Col>
            </Row>
            {/* List Header End */}
            {/* List shipment Start */}
            {shippings.length === 0 ? (
              <EmptyList message="No se encontraron registros" />
            ) : (
              <AnimatePresence>
                {shippings?.map((shipment, index) => (
                  <motion.div
                    key={index}
                    initial="hidden"
                    animate="visible"
                    transition={{
                      duration: 0.5,
                      delay: index * 0.2, // Retraso para animación secuencial
                    }}
                    variants={cardVariants}
                  >
                    <ShippingCard key={index} index={index} data={shipment} setUpdatePage={setUpdatePage} />
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
            {/* List shipment End */}
            {/* Pagination Start */}
            <div className={`d-flex flex-column justify-content-center align-items-center mt-5 mx-auto ${totalPages < 2 && 'd-none'}`}>
              <Paginate currentPage={pageView} totalPages={totalPages} setPageReducer={setPage} />
            </div>
            {/* Pagination End */}
          </div>
          {/* *** Main Page End *** */}
        </>
      )}
    </>
  );
};

export default Shippings;
